import { RichTextEditor } from '@/components/shared/RichTextEditor'
import { canManageEmailTemplates, isReadOnly } from '@/lib/permissions'
import { formatDateTime } from '@/lib/utils'
import { useThrottledCallback } from '@/hooks/useThrottledCallback'
import type { MedicationRequest } from '@/services/med-req-detail'
import {
  useCreateEmailTemplate,
  useDeleteEmailTemplate,
  useEmailTemplatesQuery,
  useFanEmailPreview,
  useSendEmailTemplate,
  useUpdateEmailTemplate
} from '@/services/messaging'
import { useOrderByHashQuery } from '@/services/orders'
import { borderRadius, colors } from '@/theme'
import type { EmailTemplate, EmailTemplateCategory } from '@/types/email-template'
import { buildTemplateData, DEFAULT_VENDOR_HASH, FAN_TEMPLATES, type FanTemplate } from '@/types/fan-email'
import { findUnresolvedVariables, formatUnresolvedVariable, interpolateTemplate } from '@/types/template-variables'
import { usePrescriberFanWorkflow } from '@/services/workflows'
import { isAxiosError } from 'axios'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import SaveIcon from '@mui/icons-material/Save'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useMemo, useRef, useState } from 'react'

interface SendFanEmailDialogProps {
  open: boolean
  medRequest: MedicationRequest
  onClose: () => void
  categoryFilter?: EmailTemplate['category']
}

function getMessagingTemplate(category: EmailTemplate['category']): FanTemplate {
  const templateId = category === 'rejection' ? 'cs-refund' : category === 'follow-up' ? 'cs-follow-up' : 'cs-on-hold'
  return FAN_TEMPLATES.find(t => t.id === templateId)!
}

function scoreTemplate(template: EmailTemplate, medication: string): number {
  if (!medication) return 0
  const medLower = medication.toLowerCase()

  let score = 0
  for (const treatment of template.treatments) {
    const treatmentLower = treatment.toLowerCase()
    if (medLower.includes(treatmentLower) || treatmentLower.includes(medLower)) {
      score += 100
      break
    }
    const medWords = medLower.split(/\s+/)
    for (const word of medWords) {
      if (word.length > 2 && treatmentLower.includes(word)) {
        score += 50
        break
      }
    }
  }

  const conditionLower = template.condition.toLowerCase()
  if (medLower.includes(conditionLower) || conditionLower.includes(medLower)) {
    score += 30
  }

  return score
}

const autocompleteFilter = createFilterOptions<EmailTemplate>({
  stringify: option => `${option.condition} ${option.name} ${option.treatments.join(' ')}`
})

const DIALOG_TITLES: Record<EmailTemplate['category'], string> = {
  fan: 'Send & schedule FAN emails',
  'follow-up': 'Send follow-up email',
  rejection: 'Send rejection email'
}

const SUCCESS_MESSAGES: Record<EmailTemplate['category'], string> = {
  fan: 'FAN email workflow triggered',
  'follow-up': 'Follow-up email sent',
  rejection: 'Rejection email sent'
}

interface NewTemplateForm {
  name: string
  condition: string
  category: EmailTemplateCategory
  treatments: string[]
}

const EMPTY_FORM: NewTemplateForm = {
  name: '',
  condition: '',
  category: 'fan',
  treatments: []
}

const DEFAULT_SENDER_EMAIL = 'hello@e-surgery.com'

const EMPTY_TEMPLATES: EmailTemplate[] = []

export default function SendFanEmailDialog({ open, medRequest, onClose, categoryFilter }: SendFanEmailDialogProps) {
  const readOnly = isReadOnly()
  const canManageTemplates = canManageEmailTemplates()
  const vendorHash = medRequest.epharmacyVendor || DEFAULT_VENDOR_HASH
  const { data: order } = useOrderByHashQuery(medRequest.orderHash)

  const templatesQuery = useEmailTemplatesQuery(open)
  const templates = templatesQuery.data ?? EMPTY_TEMPLATES
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<EmailTemplate | null>(null)
  const [editorContent, setEditorContent] = useState('')
  const [unresolvedVarsWarning, setUnresolvedVarsWarning] = useState<string[] | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [newForm, setNewForm] = useState<NewTemplateForm>({ ...EMPTY_FORM })
  const [treatmentInput, setTreatmentInput] = useState('')
  const [showSaveConfirm, setShowSaveConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const sortedTemplates = useMemo(() => {
    const medication = medRequest.orderedItem.name || ''
    let list = templates

    if (categoryFilter) {
      list = list.filter(t => t.category === categoryFilter)
    }

    // Autocomplete's groupBy requires items sharing a group (condition) to be
    // adjacent in the array — sort by each condition's best score first so
    // relevant conditions float to the top without breaking that contiguity.
    const scoreByTemplate = new Map<EmailTemplate, number>()
    const bestScoreByCondition = new Map<string, number>()
    for (const template of list) {
      const score = scoreTemplate(template, medication)
      scoreByTemplate.set(template, score)
      bestScoreByCondition.set(template.condition, Math.max(bestScoreByCondition.get(template.condition) ?? 0, score))
    }

    return [...list].sort((a, b) => {
      const groupScoreA = bestScoreByCondition.get(a.condition)!
      const groupScoreB = bestScoreByCondition.get(b.condition)!
      if (groupScoreB !== groupScoreA) return groupScoreB - groupScoreA
      if (a.condition !== b.condition) return a.condition.localeCompare(b.condition)

      const scoreA = scoreByTemplate.get(a)!
      const scoreB = scoreByTemplate.get(b)!
      if (scoreB !== scoreA) return scoreB - scoreA
      return a.name.localeCompare(b.name)
    })
  }, [templates, medRequest.orderedItem.name, categoryFilter])

  // Each category maps to one container template (fan -> cs-on-hold, etc.) —
  // its ObjectId is only knowable from a preset already bound to it.
  const templateIdByCategory = useMemo(() => {
    const map: Partial<Record<EmailTemplateCategory, string>> = {}
    for (const template of templates) {
      map[template.category] ??= template.templateId
    }
    return map
  }, [templates])

  // Scoped to the category being created so suggestions don't leak in from
  // unrelated categories (e.g. rejection templates while creating a fan one).
  const templatesForNewCategory = useMemo(
    () => templates.filter(t => t.category === newForm.category),
    [templates, newForm.category]
  )

  // Mirrors the backend's uniqueness check so we can catch duplicates before
  // hitting the API, saving a network round trip and a noisy 409 error log.
  const isDuplicateTemplateName = useMemo(() => {
    const name = newForm.name.trim().toLowerCase()
    if (!name) return false
    return templatesForNewCategory.some(t => t.name.trim().toLowerCase() === name)
  }, [templatesForNewCategory, newForm.name])
  const conditionOptions = useMemo(
    () => [...new Set(templatesForNewCategory.map(t => t.condition).filter(Boolean))].sort(),
    [templatesForNewCategory]
  )
  const treatmentOptions = useMemo(
    () => [...new Set(templatesForNewCategory.flatMap(t => t.treatments))].sort(),
    [templatesForNewCategory]
  )

  const dialogTitle = categoryFilter ? DIALOG_TITLES[categoryFilter] : 'Send email'

  const messagingTemplate = selectedEmailTemplate ? getMessagingTemplate(selectedEmailTemplate.category) : null

  const previewMutation = useFanEmailPreview()
  const sendEmailMutation = useSendEmailTemplate()
  const prescriberFanWorkflowMutation = usePrescriberFanWorkflow()
  const createTemplateMutation = useCreateEmailTemplate()
  const updateTemplateMutation = useUpdateEmailTemplate()
  const deleteTemplateMutation = useDeleteEmailTemplate()
  const queryClient = useQueryClient()

  const stateRef = useRef({ medRequest, messagingTemplate, vendorHash })
  stateRef.current = { medRequest, messagingTemplate, vendorHash }

  // Workflow-path timeline writes happen seconds after the trigger returns
  // (Temporal kicks off → queryPHP enrichment → sendEmail activity → BullMQ
  // → msgTimelineJob → PHP /timeline). One immediate invalidate + one delayed
  // refetch catches both the direct-send race and the workflow-path delay.
  const delayedRefetchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (delayedRefetchTimeout.current) clearTimeout(delayedRefetchTimeout.current)
    },
    []
  )

  const invalidateTimelines = () => {
    queryClient.invalidateQueries({
      queryKey: ['timeline', 'medication_request', medRequest.id]
    })
    queryClient.invalidateQueries({
      queryKey: ['timeline', 'order', medRequest.orderHash]
    })
  }

  const scheduleDelayedTimelineRefetch = () => {
    if (delayedRefetchTimeout.current) clearTimeout(delayedRefetchTimeout.current)
    delayedRefetchTimeout.current = setTimeout(invalidateTimelines, 3000)
  }

  const resolveContent = (raw: string) => interpolateTemplate(raw, medRequest, order)

  const fetchPreview = (content?: string) => {
    const { medRequest, messagingTemplate, vendorHash } = stateRef.current
    if (!messagingTemplate) return
    const resolved = content ? interpolateTemplate(content, medRequest, order) : content
    const data = buildTemplateData(medRequest, messagingTemplate.requiredFields, resolved)
    previewMutation.mutate({
      templateId: messagingTemplate.id,
      vendorHash,
      data: {
        ...data,
        subject: 'SUBJECT_PLACEHOLDER' // Subject is required by the API but will be overridden when sending
      }
    })
  }

  const throttledFetchPreview = useThrottledCallback((content: string) => fetchPreview(content), 1200)

  const handleTemplateSelect = (_event: unknown, template: EmailTemplate | null) => {
    setSelectedEmailTemplate(template)
    if (template) {
      setEditorContent(template.content)
      const msgTemplate = getMessagingTemplate(template.category)
      const resolved = resolveContent(template.content)
      const data = buildTemplateData(medRequest, msgTemplate.requiredFields, resolved)
      previewMutation.mutate({
        templateId: msgTemplate.id,
        vendorHash,
        data: {
          ...data,
          subject: 'SUBJECT_PLACEHOLDER'
        }
      })
    } else {
      setEditorContent('')
      previewMutation.reset()
    }
  }

  const handleEditorChange = (html: string) => {
    setEditorContent(html)
    throttledFetchPreview(html)
  }

  const doSend = async (resolved: string) => {
    if (!selectedEmailTemplate) return

    const endWithSuccess = () => {
      invalidateTimelines()
      scheduleDelayedTimelineRefetch()
      enqueueSnackbar(SUCCESS_MESSAGES[selectedEmailTemplate.category], {
        variant: 'success'
      })
      handleClose()
    }

    if (selectedEmailTemplate.category === 'fan') {
      await prescriberFanWorkflowMutation.mutateAsync({
        orderItemHash: medRequest.id,
        content: resolved,
        presetId: selectedEmailTemplate.id,
        presetName: selectedEmailTemplate.name,
        presetCategory: selectedEmailTemplate.category,
        contentEdited: contentChanged
      })
      return endWithSuccess()
    }

    if (!messagingTemplate) return
    const data = buildTemplateData(medRequest, messagingTemplate.requiredFields, resolved)

    const isGpTemplate = selectedEmailTemplate.name === 'EVA Notification Issued Treatment GP'
    const recipientEmail = isGpTemplate ? medRequest.gp?.email : medRequest.customerEmail
    if (!recipientEmail) {
      enqueueSnackbar(isGpTemplate ? 'GP email is missing' : 'Customer email is missing', { variant: 'error' })
      return
    }

    await sendEmailMutation.mutateAsync({
      templateId: messagingTemplate.id,
      vendorHash,
      data,
      to: recipientEmail,
      from: DEFAULT_SENDER_EMAIL,
      subject: messagingTemplate.subject ?? undefined,
      timeline: {
        itemHash: medRequest.id,
        orderHash: medRequest.orderHash
      }
    })

    endWithSuccess()
  }

  const handleSubmit = async () => {
    if (readOnly) return
    if (!selectedEmailTemplate) return

    const resolved = resolveContent(editorContent)

    const unresolvedMatches = findUnresolvedVariables(resolved)
    if (unresolvedMatches.length) {
      setUnresolvedVarsWarning(unresolvedMatches)
      return
    }

    await doSend(resolved)
  }

  const handleConfirmSendWithWarning = async () => {
    setUnresolvedVarsWarning(null)
    await doSend(resolveContent(editorContent))
  }

  const resetCreateForm = () => {
    setIsCreating(false)
    setNewForm({ ...EMPTY_FORM })
    setTreatmentInput('')
  }

  const handleClose = () => {
    // Guards against MUI's Dialog closing on backdrop-click/Escape while a
    // create/update request is in flight — its success continuation would
    // otherwise set state on what looks like (but isn't really) a fresh dialog.
    if (createTemplateMutation.isPending || updateTemplateMutation.isPending || deleteTemplateMutation.isPending) return

    setSelectedEmailTemplate(null)
    setEditorContent('')
    resetCreateForm()
    setShowSaveConfirm(false)
    setShowDeleteConfirm(false)
    previewMutation.reset()
    sendEmailMutation.reset()
    prescriberFanWorkflowMutation.reset()
    onClose()
  }

  const handleStartCreate = () => {
    setIsCreating(true)
    setNewForm({ ...EMPTY_FORM, category: categoryFilter || 'fan' })
  }

  const handleCreateTemplate = async () => {
    const containerTemplateId = templateIdByCategory[newForm.category]
    if (!containerTemplateId) {
      enqueueSnackbar('No container template found for this category', {
        variant: 'error'
      })
      return
    }

    if (isDuplicateTemplateName) {
      enqueueSnackbar('A template with this name already exists', { variant: 'error' })
      return
    }

    try {
      const created = await createTemplateMutation.mutateAsync({
        templateId: containerTemplateId,
        name: newForm.name,
        condition: newForm.condition,
        treatments: newForm.treatments,
        content: '',
        category: newForm.category
      })
      setSelectedEmailTemplate(created)
      setEditorContent('')
      resetCreateForm()
      previewMutation.reset()
    } catch (error) {
      const message =
        isAxiosError(error) && error.response?.status === 409
          ? ((error.response.data as { error?: string })?.error ?? 'A template with this name already exists')
          : 'Failed to create template'
      enqueueSnackbar(message, { variant: 'error' })
    }
  }

  const canCreate = Boolean(
    newForm.name.trim() &&
      newForm.condition.trim() &&
      templateIdByCategory[newForm.category] &&
      !isDuplicateTemplateName
  )

  const handleConfirmSave = async () => {
    if (!selectedEmailTemplate) return
    try {
      const updated = await updateTemplateMutation.mutateAsync({
        id: selectedEmailTemplate.id,
        name: selectedEmailTemplate.name,
        condition: selectedEmailTemplate.condition,
        treatments: selectedEmailTemplate.treatments,
        content: editorContent,
        category: selectedEmailTemplate.category
      })
      setSelectedEmailTemplate(updated)
      setShowSaveConfirm(false)
    } catch {
      enqueueSnackbar('Failed to save template', { variant: 'error' })
    }
  }

  const handleConfirmDelete = async () => {
    if (!selectedEmailTemplate) return
    try {
      await deleteTemplateMutation.mutateAsync(selectedEmailTemplate.id)
      setSelectedEmailTemplate(null)
      setEditorContent('')
      setShowDeleteConfirm(false)
      previewMutation.reset()
      enqueueSnackbar('Template deleted', { variant: 'success' })
    } catch {
      enqueueSnackbar('Failed to delete template', { variant: 'error' })
    }
  }

  const contentChanged = Boolean(selectedEmailTemplate && editorContent !== selectedEmailTemplate.content)
  // Tiptap's canonical empty doc serializes to '<p></p>', not '' — a newly
  // created template starts as '' and typing-then-deleting everything ends
  // up as '<p></p>', so both need checking before allowing a send.
  const isEditorContentEmpty = editorContent.trim() === '' || editorContent.trim() === '<p></p>'

  const isSubmitting = prescriberFanWorkflowMutation.isPending || sendEmailMutation.isPending

  return (
    <>
      <Dialog
        open={!!unresolvedVarsWarning}
        onClose={() => setUnresolvedVarsWarning(null)}
        maxWidth='xs'
        fullWidth
        PaperProps={{ sx: { borderRadius: borderRadius.lg } }}
      >
        <DialogTitle>Unfilled variables</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 1 }}>
            The following variables couldn't be resolved automatically. The email will be sent with these placeholders
            still visible:
          </Typography>
          <Box component='ul' sx={{ pl: 2, m: 0 }}>
            {unresolvedVarsWarning?.map(v => (
              <Typography
                key={v}
                component='li'
                variant='body2'
                sx={{ fontFamily: 'monospace', color: colors.status.warning }}
              >
                {formatUnresolvedVariable(v)}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant='ghost' onClick={() => setUnresolvedVarsWarning(null)}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleConfirmSendWithWarning} disabled={isSubmitting}>
            Send anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='lg'
        fullWidth
        PaperProps={{ sx: { borderRadius: borderRadius.lg } }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant='sectionTitle'>{dialogTitle}</Typography>
            <IconButton onClick={handleClose} size='small'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {isCreating ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    p: 2,
                    border: `1px solid ${colors.brand.primary}`,
                    borderRadius: borderRadius.sm,
                    bgcolor: colors.background.surface
                  }}
                >
                  <Typography variant='fieldLabel' sx={{ fontWeight: 600, color: colors.text.primary }}>
                    New template
                  </Typography>
                  <TextField
                    label='Template Name'
                    size='small'
                    value={newForm.name}
                    onChange={e => setNewForm(prev => ({ ...prev, name: e.target.value }))}
                    fullWidth
                    required
                    error={isDuplicateTemplateName}
                    helperText={isDuplicateTemplateName ? 'A template with this name already exists' : undefined}
                  />
                  <Autocomplete
                    freeSolo
                    options={conditionOptions}
                    value={newForm.condition}
                    onInputChange={(_, value) => setNewForm(prev => ({ ...prev, condition: value }))}
                    renderInput={params => <TextField {...params} label='Condition' size='small' required fullWidth />}
                  />
                  <Autocomplete
                    multiple
                    freeSolo
                    options={treatmentOptions}
                    value={newForm.treatments}
                    inputValue={treatmentInput}
                    onInputChange={(_, value) => setTreatmentInput(value)}
                    onChange={(_, value) =>
                      setNewForm(prev => ({
                        ...prev,
                        treatments: value as string[]
                      }))
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip {...getTagProps({ index })} key={option} label={option} size='small' />
                      ))
                    }
                    renderInput={params => (
                      <TextField {...params} label='Treatments' size='small' placeholder='Type and press Enter' />
                    )}
                  />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant='ghost'
                      size='small'
                      onClick={resetCreateForm}
                      disabled={createTemplateMutation.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant='primary'
                      size='small'
                      onClick={handleCreateTemplate}
                      disabled={!canCreate || createTemplateMutation.isPending}
                    >
                      {createTemplateMutation.isPending ? 'Creating...' : 'Create'}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1
                    }}
                  >
                    <Typography variant='fieldLabel'>Select email template</Typography>
                    {canManageTemplates && (
                      <Button
                        variant='ghost'
                        size='small'
                        startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={handleStartCreate}
                        sx={{ fontSize: '12px', minHeight: 0, py: 0 }}
                      >
                        New template
                      </Button>
                    )}
                  </Box>
                  <Autocomplete
                    value={selectedEmailTemplate}
                    onChange={handleTemplateSelect}
                    options={sortedTemplates}
                    loading={templatesQuery.isLoading}
                    groupBy={option => option.condition}
                    getOptionLabel={option => option.name}
                    filterOptions={autocompleteFilter}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    size='small'
                    renderOption={(props, option) => {
                      const { key, ...rest } = props
                      return (
                        <Box component='li' key={key} {...rest}>
                          <Typography sx={{ fontSize: '13px' }}>{option.name}</Typography>
                        </Box>
                      )
                    }}
                    renderGroup={params => (
                      <li key={params.key}>
                        <Box
                          sx={{
                            position: 'sticky',
                            top: '-8px',
                            px: 2,
                            py: 0.75,
                            bgcolor: colors.background.surface,
                            borderBottom: `1px solid ${colors.border.default}`,
                            zIndex: 1
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: 700,
                              color: colors.text.secondary,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {params.group}
                          </Typography>
                        </Box>
                        <ul style={{ padding: 0 }}>{params.children}</ul>
                      </li>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder='Search by condition, treatment, or template name...'
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: borderRadius.sm,
                            bgcolor: colors.background.paper
                          }
                        }}
                      />
                    )}
                    slotProps={{
                      listbox: {
                        sx: { maxHeight: 360 }
                      }
                    }}
                  />
                </Box>
              )}

              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant='fieldLabel'>Edit email</Typography>
                  </Box>
                  {canManageTemplates && selectedEmailTemplate && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {contentChanged && (
                        <Button
                          variant='ghost'
                          size='small'
                          startIcon={<SaveIcon sx={{ fontSize: '16px !important' }} />}
                          onClick={() => setShowSaveConfirm(true)}
                          sx={{
                            fontSize: '12px',
                            minHeight: 0,
                            py: 0,
                            color: colors.brand.primary
                          }}
                        >
                          Save template
                        </Button>
                      )}
                      <Button
                        variant='ghost'
                        size='small'
                        startIcon={<DeleteOutlineIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => setShowDeleteConfirm(true)}
                        sx={{
                          fontSize: '12px',
                          minHeight: 0,
                          py: 0,
                          color: colors.status.error
                        }}
                      >
                        Delete template
                      </Button>
                    </Box>
                  )}
                </Box>
                {selectedEmailTemplate && (
                  <Typography sx={{ fontSize: '11px', color: colors.text.muted }}>
                    Last updated: {formatDateTime(selectedEmailTemplate.updatedAt)}{' '}
                    {selectedEmailTemplate.updatedBy ? `, by ${selectedEmailTemplate.updatedBy.name}` : ''}
                  </Typography>
                )}
                <RichTextEditor
                  content={editorContent}
                  onChange={handleEditorChange}
                  disabled={!selectedEmailTemplate}
                  showVariables
                  placeholder={
                    selectedEmailTemplate ? 'Edit the template content...' : 'Select a template to get started...'
                  }
                  height={340}
                />
              </Box>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant='fieldLabel' sx={{ display: 'block', mb: 1 }}>
                Preview email
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  border: `1px solid ${colors.border.default}`,
                  borderRadius: borderRadius.sm,
                  bgcolor: colors.background.paper,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 400
                }}
              >
                {previewMutation.isPending ? (
                  <CircularProgress size={24} />
                ) : previewMutation.isError ? (
                  <Typography
                    variant='body2'
                    sx={{
                      color: colors.status.error,
                      textAlign: 'center',
                      p: 2
                    }}
                  >
                    Failed to load preview
                  </Typography>
                ) : previewMutation.data ? (
                  <Box
                    component='iframe'
                    srcDoc={previewMutation.data}
                    sx={{ width: '100%', height: '100%', border: 'none' }}
                    title='Email Preview'
                  />
                ) : (
                  <Typography variant='body2' sx={{ color: colors.text.muted, fontStyle: 'italic' }}>
                    Select a template to preview...
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            gap: 1,
            bgcolor: colors.background.surface,
            borderTop: `1px solid ${colors.border.default}`
          }}
        >
          <Button variant='ghost' onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleSubmit}
            disabled={
              readOnly || !selectedEmailTemplate || isEditorContentEmpty || isSubmitting || previewMutation.isPending
            }
          >
            {isSubmitting
              ? 'Sending...'
              : selectedEmailTemplate?.category === 'fan'
                ? 'Send & schedule FAN emails'
                : 'Send email'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showSaveConfirm} onClose={() => setShowSaveConfirm(false)} maxWidth='xs' fullWidth>
        <DialogTitle>Save template</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            This will permanently update the template <strong>{selectedEmailTemplate?.name}</strong>. The previous
            content will be replaced with the current editor content.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant='ghost' onClick={() => setShowSaveConfirm(false)} disabled={updateTemplateMutation.isPending}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleConfirmSave} disabled={updateTemplateMutation.isPending}>
            {updateTemplateMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showDeleteConfirm}
        onClose={() => {
          if (!deleteTemplateMutation.isPending) setShowDeleteConfirm(false)
        }}
        maxWidth='xs'
        fullWidth
        PaperProps={{ sx: { borderRadius: borderRadius.lg } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon sx={{ color: colors.status.error }} />
            <Typography variant='sectionTitle'>Delete template?</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2 }}>
            You're about to permanently delete this template for everyone. This can't be undone.
          </Typography>
          <Box
            sx={{
              p: 1.5,
              border: `1px solid ${colors.border.default}`,
              borderRadius: borderRadius.sm,
              bgcolor: colors.background.surface,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5
            }}
          >
            <Typography variant='body2' sx={{ fontWeight: 600 }}>
              {selectedEmailTemplate?.name}
            </Typography>
            {selectedEmailTemplate?.condition && (
              <Typography variant='body2' sx={{ color: colors.text.secondary }}>
                Condition: {selectedEmailTemplate.condition}
              </Typography>
            )}
            {!!selectedEmailTemplate?.treatments.length && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {selectedEmailTemplate.treatments.map(treatment => (
                  <Chip key={treatment} label={treatment} size='small' />
                ))}
              </Box>
            )}
            <Typography sx={{ fontSize: '11px', color: colors.text.muted, mt: 0.5 }}>
              Last updated: {selectedEmailTemplate ? formatDateTime(selectedEmailTemplate.updatedAt) : ''}
              {selectedEmailTemplate?.updatedBy ? `, by ${selectedEmailTemplate.updatedBy.name}` : ''}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant='ghost'
            onClick={() => setShowDeleteConfirm(false)}
            disabled={deleteTemplateMutation.isPending}
            autoFocus
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleConfirmDelete}
            disabled={deleteTemplateMutation.isPending}
            startIcon={
              deleteTemplateMutation.isPending ? (
                <CircularProgress size={14} sx={{ color: 'inherit' }} />
              ) : (
                <DeleteOutlineIcon sx={{ fontSize: '16px !important' }} />
              )
            }
            sx={{
              bgcolor: colors.status.error,
              '&:hover': { bgcolor: colors.status.error, filter: 'brightness(0.9)' }
            }}
          >
            {deleteTemplateMutation.isPending ? 'Deleting...' : 'Delete template'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
